/**
 * Which PvP season is on right now, feed first (2026-09-28).
 *
 * WHY. The Profile's PvP season card read its season from a hardcoded table
 * (data/pvpSeasons) that somebody had to append to every three months. Nobody
 * did for Season 28, so from 8 Sep 2026 the card said "No active season" for
 * three weeks while the events feed, on the same screen, was already carrying
 * "Twilight Trails" as a live `season` event. The feed is what the admin
 * events page curates (LeekDuck rows the cron adopts; hide / end early /
 * change end all land there), so it is the season's one source of truth and
 * the table is demoted to a fallback.
 *
 * RULES
 *   - The live season is the `category === "season"` feed event whose window
 *     covers `now` (device clock, floating GO wall-clock via eventRecency, the
 *     same rule the Calendar and radars use). Two covering seasons (a feed
 *     overlap at the hand-over hour) resolve to the later-starting one.
 *   - Its number comes from the table by name; failing that, a feed season
 *     that starts where the table's last row ends is that row's number + 1;
 *     otherwise null, and the card shows the name alone. Never guess further
 *     than one season past the table.
 *   - No live feed season (feed down, cache empty, gap between seasons): fall
 *     back to the table, exactly as before.
 */
import {
  PVP_SEASONS,
  getCurrentPvpSeason,
  pvpSeasonProgress,
  type PvpSeason,
  type PvpSeasonProgress,
} from "@/data/pvpSeasons";
import { eventEndMs, eventStartMs } from "./eventRecency";

/** The slice of a /api/events/live row this module reads. */
export interface SeasonFeedEvent {
  name: string;
  category?: string | null;
  start?: string | null;
  end?: string | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** "Season: Twilight Trails" and "Twilight Trails Season" both read as the name. */
export function cleanSeasonName(raw: string): string {
  return raw
    .replace(/^\s*season\s*[:\-–]\s*/i, "")
    .replace(/\s+season\s*$/i, "")
    .trim();
}

/**
 * Number for a feed season: by name from the table, else +1 when it picks up
 * where the table's last row ends (within a day), else null.
 */
export function seasonNumberFor(name: string, startMs: number): number | null {
  const key = cleanSeasonName(name).toLowerCase();
  const byName = PVP_SEASONS.find((s) => s.name.toLowerCase() === key);
  if (byName) return byName.number;
  const last = PVP_SEASONS[PVP_SEASONS.length - 1];
  if (last && last.number != null && Math.abs(startMs - last.endMs) <= DAY_MS) {
    return last.number + 1;
  }
  return null;
}

/** The feed's live season covering `now`, or null when it carries none. */
export function seasonFromFeed(events: readonly SeasonFeedEvent[], now: number): PvpSeason | null {
  const live = events
    .filter((e) => e.category === "season" && typeof e.name === "string" && e.name.trim().length > 0)
    .map((e) => ({ e, startMs: eventStartMs(e), endMs: eventEndMs(e) }))
    .filter(
      (x) =>
        Number.isFinite(x.startMs) &&
        Number.isFinite(x.endMs) &&
        x.endMs > x.startMs &&
        now >= x.startMs &&
        now < x.endMs,
    )
    .sort((a, b) => a.startMs - b.startMs);
  const pick = live[live.length - 1];
  if (!pick) return null;
  const name = cleanSeasonName(pick.e.name);
  return { number: seasonNumberFor(name, pick.startMs), name, startMs: pick.startMs, endMs: pick.endMs };
}

/** Season progress: the feed's live season, else the hardcoded table. */
export function resolvePvpSeason(
  events: readonly SeasonFeedEvent[] | undefined,
  now: number = Date.now(),
): PvpSeasonProgress {
  const fromFeed = events ? seasonFromFeed(events, now) : null;
  if (fromFeed) return pvpSeasonProgress(fromFeed, now);
  return getCurrentPvpSeason(now);
}
